import { useCallback, useRef } from 'react';
import { subscribeToPush } from '@/lib/push-subscribe';

const STORAGE_KEY = 'mw_push_prompted';

/**
 * GROWTH-003: Hook that triggers browser push notification opt-in
 * on the user's first follow/save/bookmark action. Prompts only once
 * per browser (tracked via localStorage).
 */
export function usePushOptIn() {
  const promptedRef = useRef(false);

  const maybePromptPush = useCallback(async () => {
    // Skip if already prompted this session or ever
    if (promptedRef.current) return;
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    // Skip if push not supported or already granted
    if (!('Notification' in window) || !('PushManager' in window)) return;
    if (Notification.permission === 'granted') return;
    if (Notification.permission === 'denied') {
      localStorage.setItem(STORAGE_KEY, 'denied');
      return;
    }

    // Mark as prompted before async work
    promptedRef.current = true;
    localStorage.setItem(STORAGE_KEY, 'pending');

    // Small delay so the follow/save action feels instant
    await new Promise((r) => setTimeout(r, 1500));

    const result = await subscribeToPush();
    localStorage.setItem(STORAGE_KEY, result);
  }, []);

  return { maybePromptPush };
}
